import type { Character } from '../domain/character.ts';
import type { Item } from '../content/types.ts';
import { itemEffects } from '../rules/inventory.ts';
import { findSkillRank } from '../domain/character.ts';
import type { StatBlock, StatId } from '../domain/stats.ts';
import type { CheckContext, Effect, ResolvedModifiers } from '../rules/effects.ts';
import {
  advantageSourcesFor,
  checkModifiersFor,
  disadvantageSourcesFor,
  emptyResolvedModifiers,
  resolveModifiers,
  scopeApplies,
  statModifierSources,
} from '../rules/effects.ts';
import { resolveEnhancedStats } from '../rules/enhancedStats.ts';
import { statModForScore } from '../rules/statModifiers.ts';
import type { RollMode } from '../rules/rollModes.ts';
import { resolveRollMode } from '../rules/rollModes.ts';
import type { RollEffectSource } from '../rules/rollHistory.ts';
import { STAT_LABELS } from '../domain/stats.ts';

/**
 * Character + currently active Effects -> effective mechanical values.
 *
 * ONE object, built once, that every screen and service reads. The Roll Tray,
 * the Attacks panel, Vitals, Evade, damage and the future Buff/Debuff and
 * equipment systems all take their numbers from here, so they cannot disagree.
 *
 * The stored Character is never mutated. A temporary +3 Dex from a Buff changes
 * `enhancedStats` and every Mod derived from it, while `character` still holds
 * the crawler's own Stats, and a temporary +2 Skill Rank changes
 * `effectiveSkillRank` while `baseSkillRank` still reports what is saved.
 */
export interface EffectiveCharacter {
  /** The stored crawler. Never changed by Effects. */
  readonly character: Character;
  readonly effects: readonly Effect[];
  readonly modifiers: ResolvedModifiers;
  /** Unenhanced Stats plus every Stat modifier. Resolved once. */
  readonly enhancedStats: StatBlock;

  /** Stat Mod taken from the ENHANCED score (RULE DCC-STAT-MOD-002). */
  statMod(stat: StatId): number;
  /** The Rank actually stored on the character sheet. */
  baseSkillRank(skillId: string): number;
  /** The stored Rank plus temporary modifiers, floored at Rank 0 for Checks. */
  effectiveSkillRank(skillId: string): number;
  /** Bonus only, for showing "Rank 5 (+2)" in the interface. */
  skillRankBonus(skillId: string): number;
  /**
   * Folds the player's manual choice together with the Effect sources that
   * apply to this Check, then hands the lot to the shared roll-mode rules.
   * The manual choice is ONE source, never a replacement for the others.
   */
  rollMode(manual: RollMode, context: CheckContext): RollMode;
  /**
   * Every Effect that touched a roll of this shape, ready to be frozen into the
   * roll history. Built from the same resolved contributions the numbers came
   * from, so an explanation can never disagree with the arithmetic.
   */
  effectSourcesFor(context: CheckContext): RollEffectSource[];
  /**
   * Flat modifiers applied to the Check total itself, e.g. Fatigued's -1 on all
   * Checks (Rules p.45). Returned as components the Check machinery already
   * understands, so no new arithmetic is introduced.
   */
  checkComponentsFor(context: CheckContext): { label: string; value: number }[];
}

/**
 * RULE DCC-INV-002 - equipped items contribute here, alongside Buffs and
 * Debuffs, through the SAME resolver.
 *
 * Armour DR is therefore an ordinary `damage-resistance` Modifier and reaches
 * `mitigateDamage` by the path a Buff's DR already takes. There is deliberately
 * no second DR calculation and no equipment-shaped special case downstream.
 *
 * Pass `lookupItem` to include worn gear. Callers that have no catalogue - a
 * pure rules test, say - omit it and get Buffs and Debuffs only.
 */
export function buildEffectiveCharacter(
  character: Character,
  effects: readonly Effect[] = [],
  lookupItem?: (itemId: string) => Item | undefined,
): EffectiveCharacter {
  const all =
    lookupItem === undefined
      ? effects
      : [...effects, ...itemEffects(character.inventory, (id) => { const item = lookupItem(id); return item?.petGear ? undefined : item; })];
  const modifiers = all.length === 0 ? emptyResolvedModifiers() : resolveModifiers(all);
  const enhancedStats = resolveEnhancedStats(
    character.unenhancedStats,
    statModifierSources(modifiers),
  );

  return {
    character,
    effects: all,
    modifiers,
    enhancedStats,

    statMod(stat) {
      return statModForScore(enhancedStats[stat]);
    },
    baseSkillRank(skillId) {
      return findSkillRank(character, skillId);
    },
    effectiveSkillRank(skillId) {
      // Rank 0 is the shared untrained boundary. Effects may contribute a
      // negative Skill-Rank modifier, but a negative Rank is not a legal Skill
      // Check input and must never escape this derived-state authority. Keep
      // the raw contribution available through `skillRankBonus()` for display
      // and provenance; only the effective playable Rank is floored.
      return Math.max(0, Math.min(
        character.floor < 6 ? 15 : (character.skillRankLimits?.[skillId] ?? 15),
        findSkillRank(character, skillId) + (modifiers.skillRankBonuses[skillId] ?? 0),
      ));
    },
    skillRankBonus(skillId) {
      return modifiers.skillRankBonuses[skillId] ?? 0;
    },
    checkComponentsFor(context) {
      return checkModifiersFor(modifiers, context).map((m) => ({
        label: m.label,
        value: m.amount,
      }));
    },
    effectSourcesFor(context) {
      const sources: RollEffectSource[] = [];

      if (context.stat) {
        for (const c of modifiers.contributions.stat[context.stat]) {
          sources.push({
            kind: 'modifier',
            label: c.label,
            sourceId: c.sourceId,
            target: `${STAT_LABELS[context.stat].abbr} score`,
            amount: c.amount,
          });
        }
      }

      if (context.skillId) {
        for (const c of modifiers.contributions.skillRank[context.skillId] ?? []) {
          sources.push({
            kind: 'modifier',
            label: c.label,
            sourceId: c.sourceId,
            target: 'Skill Rank',
            amount: c.amount,
          });
        }
      }

      for (const m of checkModifiersFor(modifiers, context)) {
        sources.push({
          kind: 'modifier',
          label: m.label,
          sourceId: m.sourceId,
          target: 'Check total',
          amount: m.amount,
        });
      }

      if (context.kind === 'evade') {
        for (const c of modifiers.contributions.evade) {
          sources.push({
            kind: 'modifier',
            label: c.label,
            sourceId: c.sourceId,
            target: 'Evade',
            amount: c.amount,
          });
        }
      }

      for (const source of modifiers.advantageSources) {
        if (!scopeApplies(source.scope, context)) continue;
        sources.push({
          kind: 'advantage',
          label: source.label,
          sourceId: source.sourceId,
          target: null,
          amount: null,
        });
      }
      for (const source of modifiers.disadvantageSources) {
        if (!scopeApplies(source.scope, context)) continue;
        sources.push({
          kind: 'disadvantage',
          label: source.label,
          sourceId: source.sourceId,
          target: null,
          amount: null,
        });
      }

      return sources;
    },
    rollMode(manual, context) {
      const advantage =
        advantageSourcesFor(modifiers, context) + (manual === 'advantage' ? 1 : 0);
      const disadvantage =
        disadvantageSourcesFor(modifiers, context) + (manual === 'disadvantage' ? 1 : 0);
      return resolveRollMode(advantage, disadvantage);
    },
  };
}
